import { Button, Radio, RadioGroup } from '@odekoteam/doppio';
import { Box, Flex, Heading, Paragraph } from 'theme-ui';

import {
  ModalFooterActions,
  ModalHeaderStyle,
} from '@/components/Brand/Styles';
import {
  BannerDestination,
  creativeFieldName,
  CreativeTarget,
  hasArtwork,
} from '@/components/Collection/categoryBannerMarkets';
import CategoryBannerDestination, {
  CollectionChoices,
} from '@/components/Collection/CategoryBannerDestination';
import CreativeImages from '@/components/Collection/CreativeImages';
import Modal from '@/components/Modal';
import {
  CategoryBannerDestinationType,
  CategoryBannerShippingBehavior,
} from '@/graphql/types/generated';

/**
 * The artwork a category banner needs for one market. Dimensions are fixed by
 * the storefront's layout, not by the market, so they live here rather than
 * being passed in per call site.
 */
export const BANNER_IMAGE_SPECS = [
  { key: 'desktopImage', label: 'Desktop', width: 2288, height: 480 },
  { key: 'mobileImage', label: 'Mobile', width: 792, height: 320 },
] as const;

const SHIPPING_IMAGE_SPECS = [
  { key: 'shippingDesktopImage', label: 'Desktop', width: 2288, height: 480 },
  { key: 'shippingMobileImage', label: 'Mobile', width: 792, height: 320 },
] as const;

/**
 * Three exclusive states, so a radio rather than a checkbox and a disclosure —
 * those would let a market both hide the banner and carry creative for it.
 */
export const SHIPPING_CHOICES = [
  {
    value: CategoryBannerShippingBehavior.Inherit,
    label: 'Show the same artwork',
  },
  {
    value: CategoryBannerShippingBehavior.Override,
    label: 'Show different artwork',
  },
  { value: CategoryBannerShippingBehavior.Hide, label: 'Do not show a banner' },
];

export type CategoryBannerMarketModalProps = {
  visible: boolean;
  marketName: string;
  /** The default or the market this modal edits; field names are built from it. */
  target: CreativeTarget;
  desktopImage: string;
  mobileImage: string;
  shippingBehavior: CategoryBannerShippingBehavior;
  shippingDesktopImage: string;
  shippingMobileImage: string;
  onShippingBehaviorChange: (next: CategoryBannerShippingBehavior) => void;
  destinationType: CategoryBannerDestinationType | null;
  destinationRef: string | null;
  collections: CollectionChoices;
  onDestinationChange: (next: BannerDestination) => void;
  customDestinationsEnabled?: boolean;
  disabled?: boolean;
  pendingFiles?: Map<string, File>;
  onImageChange: (fieldName: string, key: string) => void;
  onFileSelect?: (fieldName: string, file: File | null) => void;
  onDone: () => void;
  /** Distinct from `onDone`: the caller reverts the market to how it was when the modal opened. */
  onCancel: () => void;
};

/**
 * A market's own artwork, opened from its row in the overrides list.
 *
 * Hiding a market is not offered here on purpose: it is an action on the
 * market's presence, not a property of its artwork, and putting it beside the
 * shipping-only "do not show" was the two-hides confusion the plan warned of.
 * The editor's hidden-markets section owns it instead.
 *
 * Fields are named by {@link CreativeTarget} rather than by market id, because
 * the pending-files map is keyed by field name and needs those names unique
 * across the whole form.
 */
export default function CategoryBannerMarketModal({
  visible,
  marketName,
  target,
  desktopImage,
  mobileImage,
  shippingBehavior,
  shippingDesktopImage,
  shippingMobileImage,
  onShippingBehaviorChange,
  destinationType,
  destinationRef,
  collections,
  onDestinationChange,
  customDestinationsEnabled,
  disabled,
  pendingFiles,
  onImageChange,
  onFileSelect,
  onDone,
  onCancel,
}: CategoryBannerMarketModalProps) {
  const values: Record<string, string> = {
    desktopImage,
    mobileImage,
    shippingDesktopImage,
    shippingMobileImage,
  };
  const overrides =
    shippingBehavior === CategoryBannerShippingBehavior.Override;
  // A market only belongs in the overrides list because it has its own
  // artwork, so leaving here with one image or none would confirm a market
  // that no longer qualifies for the list it is in. Closing without artwork
  // is still possible — Cancel is never gated on it.
  const canConfirm = hasArtwork({ desktopImage, mobileImage });

  return (
    <Modal
      visible={visible}
      contentStyle={{
        boxSizing: 'border-box',
        alignSelf: 'center',
        height: 'auto',
        maxHeight: '80%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Heading as="h3" sx={ModalHeaderStyle}>
        {marketName} artwork
      </Heading>
      <Flex sx={{ flexDirection: 'column', overflowY: 'auto' }}>
        <CreativeImages
          title="Category Banner"
          disabled={disabled}
          pendingFiles={pendingFiles}
          onFileSelect={onFileSelect}
          onImageChange={onImageChange}
          images={BANNER_IMAGE_SPECS.map((spec) => ({
            label: spec.label,
            value: values[spec.key],
            fieldName: creativeFieldName(target, spec.key),
            requiredWidth: spec.width,
            requiredHeight: spec.height,
          }))}
        />
        <Box sx={{ px: 6, pb: 4 }}>
          <Heading variant="headingSm">Shipping customers</Heading>
          <Paragraph
            variant="paragraphSm"
            sx={{ color: 'neutralDark', mb: 2, mt: 1 }}
          >
            What customers who get this market&rsquo;s products shipped see
          </Paragraph>
          <RadioGroup
            name={`${marketName}-shipping-behavior`}
            value={shippingBehavior}
            onChange={(next: string) =>
              onShippingBehaviorChange(next as CategoryBannerShippingBehavior)
            }
          >
            {SHIPPING_CHOICES.map((choice) => (
              <Radio key={choice.value} value={choice.value}>
                {choice.label}
              </Radio>
            ))}
          </RadioGroup>
        </Box>
        <Box sx={{ px: 6, pb: 4 }}>
          <Heading variant="headingSm">Link</Heading>
          <Paragraph
            variant="paragraphSm"
            sx={{ color: 'neutralDark', mb: 2, mt: 1 }}
          >
            Where this market&rsquo;s customers go when they select the banner
          </Paragraph>
          <CategoryBannerDestination
            name={`${marketName}-destination`}
            destinationType={destinationType}
            destinationRef={destinationRef}
            collections={collections}
            disabled={disabled}
            customDestinationsEnabled={customDestinationsEnabled}
            onChange={onDestinationChange}
          />
        </Box>
        {overrides && (
          <CreativeImages
            title="Shipping artwork"
            disabled={disabled}
            pendingFiles={pendingFiles}
            onFileSelect={onFileSelect}
            onImageChange={onImageChange}
            images={SHIPPING_IMAGE_SPECS.map((spec) => ({
              label: spec.label,
              value: values[spec.key],
              fieldName: creativeFieldName(target, spec.key),
              requiredWidth: spec.width,
              requiredHeight: spec.height,
            }))}
          />
        )}
      </Flex>
      <Flex sx={ModalFooterActions}>
        <Button variant="ghost" isDisabled={disabled} onPress={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={disabled || !canConfirm}
          onPress={onDone}
        >
          Done
        </Button>
      </Flex>
    </Modal>
  );
}
